import { useQuery } from "@tanstack/react-query";

import { listLatestMeasurements } from "@/adapters/api";
import type { LatestMeasurements } from "../types.ts";
import {
	byStageId,
	rollupsByStageId,
	toMeasurement,
	toRollup,
} from "../utils/map.ts";
import { measurementKeys } from "../utils/query-keys.ts";

function useLatestQuery<T>(
	constructionId: string | undefined,
	select: (latest: LatestMeasurements) => T,
) {
	return useQuery({
		queryKey: measurementKeys.latest(constructionId),
		queryFn: async (): Promise<LatestMeasurements> => {
			// biome-ignore lint/style/noNonNullAssertion: guarded by `enabled`
			const response = await listLatestMeasurements(constructionId!);
			return {
				measurements: response.items.map(toMeasurement),
				rollups: (response.rollups ?? []).map(toRollup),
			};
		},
		select,
		enabled: !!constructionId,
		staleTime: 30_000,
	});
}

function selectMeasurements(latest: LatestMeasurements) {
	return byStageId(latest.measurements);
}

function selectRollups(latest: LatestMeasurements) {
	return rollupsByStageId(latest.rollups);
}

export function useLatestMeasurements(constructionId: string | undefined) {
	return useLatestQuery(constructionId, selectMeasurements);
}

export function useLatestRollups(constructionId: string | undefined) {
	return useLatestQuery(constructionId, selectRollups);
}
